import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {
  EmbeddedFieldAutoUpdateFunction,
  EmbeddedFieldCallbackFunction,
  EmbeddedFieldModel
} from "../../models/embedded-field/embedded-field.model";
import {RequestService} from "../../services/request/request.service";
import {UtilService} from "../../services/util/util.service";
import {TableMetadataService} from "../../services/table-metadata/table-metadata.service";
import * as fastDeepEqual from 'fast-deep-equal/es6';
import {FeatureFlagsService} from "../../services/feature-flag/feature-flags.service";

/**
 * A component that can represent any field on a form, optionally backed by a text field. Used for template values, etc.
 */
@Component({
  selector: 'sb-knockout-nuclear-field',
  templateUrl: './knockout-nuclear-field.component.html',
  styleUrls: ['./knockout-nuclear-field.component.scss']
})
export class KnockoutNuclearFieldComponent implements EmbeddedFieldModel, OnInit {

  // Debugging
  debugLastEvent: string = "";
  debugLastContext: string[] = [];
  debugLastValue: any;
  debugLastValueType: any;
  debugLastValueTypeArray: boolean;
  debugFieldType: string = "";
  debugFieldRenderer: string = "";

  @ViewChild('target')
  target: ElementRef<HTMLElement>;

  @Input()
  debug = false;

  // Explicit specification of table, field and id

  @Input()
  table: string; // custom.field.table, e.g. User

  @Input()
  field: string; // custom.field.field, e.g. FirstName

  @Input()
  id: string; // custom.field.id, e.g. 21b390f8219211ebba6032978ad6917f

  // Specification of paths to fields that contain values for table, field

  @Input()
  tablePath: string; // custom.field.table.path, e.g. Template.Table

  @Input()
  fieldPath: string; // custom.field.field.path e.g. Field

  // Specification of table and field in one path

  @Input()
  path: string; // custom.field.path, e.g. Manager.Email

  @Input()
  mirrorPath: string; // custom.field.mirror.path, e.g. Value

  @Input()
  renderer: string; // custom.field.renderer, e.g. choice

  @Input()
  editable: boolean; // custom.field.editable, e.g. true

  @Input()
  rendererProperties: any = {};

  @Input()
  initAngularComponent: EmbeddedFieldCallbackFunction;

  @Input()
  updateAngularComponent: EmbeddedFieldCallbackFunction;

  @Input()
  autoUpdateAngularComponent: EmbeddedFieldAutoUpdateFunction;

  @Output()
  fieldRenderInfo: EventEmitter<string> = new EventEmitter<any>();

  // Target field details

  private viewDefinition: any;

  private viewDefinitionFields: any;

  private tableDefinition: any;

  private fieldDefinition: any;

  private record: any;

  // Internal state

  private current: any; // current record of the form on which this component is displayed

  private currentForm: any; // current form on which this component is displayed

  private knockoutTemplateRendered = false;

  private fieldKnockoutSubscription: any;

  private mirrorKnockoutSubscription: any;

  private fieldObservable: any;

  private mirrorObservable: any;

  private convertToMirror = {
    boolean: (value: any) => value.toString(),
    field_array: (value: any) => JSON.stringify(value),
    list: (value: any) => JSON.stringify(value),
    many_to_many: (value: any) => {
      let result = Array.isArray(value) ? value.map(v => v.id) : [];
      return JSON.stringify(result);
    },
    properties: (value: any) => JSON.stringify(value),
    reference: (value: any) => {
      return value?.id;
    },
    array_string: (value: any) => JSON.stringify(value),
    table_array: (value: any) => JSON.stringify(value),
    duration: (value: any) => String(value),
    generic_reference: (value: any) => {
      return value?.id;
    },
    generic_reference_v2: (value: any) => {
      return value?.id;
    },
    date_time: (value: any) => {
      return (typeof value === "string") ? value : value.toISOFormat();
    }
  };

  private convertToField = {
    boolean: (value: any) => this.utilService.toBoolean(value),
    field_array: (value: any) => value === "" ? [] : JSON.parse(value),
    list: (value: any) => value === "" ? [] : JSON.parse(value),
    many_to_many: (value: any) => {
      let ids = (value === "" || value == null) ? [] : JSON.parse(value);
      return ids.map(v => {
        return {
          id: v,
          value: {
            readOnly: false,
            required: false,
            value: v
          },
          _tableClass: ''
        };
      });
    },
    properties: (value: any) => value === "" ? [] : JSON.parse(value),
    reference: (value: any) => {
      return {
        id: value,
        value: {
          readOnly: false,
          required: false,
          value: value
        },
        _tableClass: ''
      };
    },
    array_string: (value: any) => value === "" ? [] : JSON.parse(value),
    table_array: (value: any) => value === "" ? [] : JSON.parse(value),
    duration: (value: any) => value == null ? 0 : Number(value),
    generic_reference: (value: any) => {
      const parts = !!value ? value.split(":") : ['', ''];
      return {
        id: parts[0],
        value: {
          readOnly: false,
          required: false,
          value: parts[0]
        },
        _tableClass: parts[1]
      };
    },
    generic_reference_v2: (value: any) => {
      const parts = !!value ? value.split(":") : ['', ''];
      return {
        id: parts[0],
        value: {
          readOnly: false,
          required: false,
          value: parts[0]
        },
        _tableClass: parts[1]
      };
    },
    date_time: (value: any) => {
      return (window as any).DateTime(value);
    }
  };

  private fieldSettingCallback = {
    many_to_many: () => {
      const listGrid = $(this.target.nativeElement).find("sb-ng-table-grid")[0];
      if (listGrid) {
        (listGrid as any).updateRelatedListIds = new Date().getTime().toString(); // Has to be unique
        (listGrid as any).embeddedListRefresh = new Date().getTime().toString(); // Has to be unique
      }
    }
  };

  constructor(private requestService: RequestService,
              private utilService: UtilService,
              private tableMetadataService: TableMetadataService,
              private featureFlagsService: FeatureFlagsService) {
    this.updateAngularComponent = this.updateComponent.bind(this);
  }

  ngOnInit(): void {
    // Inputs can either be directly set on the component or come in through the renderer properties.
    this.tablePath = this.tablePath || this.rendererProperties["custom.field.table.path"];
    this.fieldPath = this.fieldPath || this.rendererProperties["custom.field.field.path"];
    this.path = this.path || this.rendererProperties["custom.field.path"];
    this.mirrorPath = this.mirrorPath || this.rendererProperties["custom.field.mirror.path"];
    this.table = this.table || this.rendererProperties["custom.field.table"];
    this.field = this.field || this.rendererProperties["custom.field.field"];
    this.id = this.id || this.rendererProperties["custom.field.id"];
    this.renderer = this.renderer || this.rendererProperties["custom.field.renderer"];
    this.editable = Boolean((UtilService.defaultBoolean(this.editable) === true) || (UtilService.defaultBoolean(this.rendererProperties["custom.field.editable"]) === true) || false);

    this.debug = this.featureFlagsService.isOn("custom-component-debugging-display");
  }

  private async updateComponent(current: any, eventName?: string, eventContext?: any) {

    eventContext = eventContext || {};
    this.debugLastEvent = eventName;
    this.debugLastContext = Object.keys(eventContext);

    if (eventName == "onLoad") {
      await this.handleTableChange(current, eventName, eventContext, true);
    } else if (eventName == "onChange") {
      await this.handleFieldChange(current, eventName, eventContext);
    }
  }

  private async handleTableChange(current: any, eventName?: string, eventContext?: any, force: boolean = false) {

    await this.configureContext(current, eventName, eventContext, force);

    if (this.table) {

      const [changedFields, relevantFieldHasChanged] = this.changedFieldsIncludesTable(current);

      if (!this.viewDefinition || force || relevantFieldHasChanged) {

        const data: any = {
          includeSystemFields: true,
          includeAllRelationships: true,
          id: this.id,
        };

        const targetResult = await this.requestService.createRequest()
          .controller('servicely.core.CustomField')
          .method(this.id ? "get" : "metadata")
          .view("get")
          .table(this.table)
          .data(data)
          .execute();

        this.viewDefinition = (targetResult as any).viewDefinition;
        this.tableDefinition = (targetResult as any).tableDefinition;
        this.record = (targetResult as any).data;

        this.viewDefinitionFields = this.buildFieldMap(this.viewDefinition.containers);
        this.fieldDefinition = this.buildFieldDefinition();
      }

      await this.renderKnockoutTemplate(force || relevantFieldHasChanged);
    }
  }

  private async handleFieldChange(current: any, eventName?: string, eventContext?: any) {

    const viewModel = eventContext['viewModel'];
    const fieldName = viewModel?.getName();

    if (this.fieldHasChanged(fieldName)) {
      if (this.considerFieldValue()) {

        await this.configureContext(current, eventName, eventContext);

        const newField = viewModel?.value();

        // If switching fields we wipe out the current value of the mirror
        if (this.field !== newField) {
          if (this.mirrorObservable) {
            this.mirrorObservable(null);
          }
        }

        this.field = newField;
        this.fieldDefinition = this.buildFieldDefinition();
      }

      await this.renderKnockoutTemplate(true);
    } else if (this.tableHasChanged(fieldName)) {
      await this.handleTableChange(current, eventName, eventContext);
    }
  }

  private async renderKnockoutTemplate(force: boolean = false) {
    if (!this.knockoutTemplateRendered || force) {

      if (this.fieldKnockoutSubscription) {
        this.fieldKnockoutSubscription.dispose();
        this.fieldKnockoutSubscription = null;
        this.fieldObservable = null;
      }

      if (this.mirrorKnockoutSubscription) {
        this.mirrorKnockoutSubscription.dispose();
        this.mirrorKnockoutSubscription = null;
        this.mirrorObservable = null;
      }

      if (this.target) {
        const element = this.target.nativeElement;

        if (this.field) {
          const bridge = (window as any).KO_FIELD_DEFINITION_BRIDGE;
          const model = {}; // Nothing needed
          const properties = this.utilService.clone(this.fieldDefinition);
          const recordField = this.utilService.clone(this.record[this.field]);

          // properties.properties should be of type ViewModelProperties but usually isn't.
          // We delete it so that it gets built in the *standalone* functions
          if (properties?.properties) {
            delete properties.properties;
          }

          // need to set these properties if there is an existing value
          if (recordField) {
            properties.availableValues = recordField.availableValues;
              properties.initialValue = recordField.value;
              properties.initialDisplayValue = recordField.displayValue;
          }

          // If the mirror field contains a value then use that instead.
          if (this.mirrorPath) {
            const value = this.currentForm.data[this.mirrorPath].value();
              // Don't set the field value if it contains a variable reference (these are used in scheduled templates)
              if (value != null && !value?.includes("${")) {
              const converter = this.convertToField[this.fieldDefinition.relationType] || this.convertToField[this.fieldDefinition.fieldType];
              let modifiedValue = converter ? converter(value) : value;
              properties.initialValue = modifiedValue;
              properties.initialDisplayValue = null;
            }
          }

          const bindingContext = ['reference', 'many_to_many', 'generic_reference', 'generic_reference_v2'].includes(this.fieldDefinition.relationType)
            ? bridge.createStandaloneReferenceFieldDefinition(model, properties)
            : bridge.createStandaloneFieldDefinition(model, properties);

          const koFieldViewModel = bindingContext.data[this.field] || bindingContext.data[this.normaliseFieldName(this.field)];
          const koFieldDefinition = bindingContext.fieldDefinition;
          const koRecord = bindingContext.record;

          // Capture changes to the field and write to the mirror. Format depends on field type.
          this.fieldObservable = koFieldViewModel.value;
          this.fieldKnockoutSubscription = this.fieldObservable.subscribe((value) => {
            if (this.mirrorObservable) {
              if (this.fieldDefinition) {
                this.debugLastValueType = typeof value;
                this.debugLastValueTypeArray = Array.isArray(value);
                this.debugLastValue = JSON.stringify(value, null ,2);

                // Don't set the mirror value if it contains a variable reference (these are used in scheduled templates)
                if (!this.mirrorObservable()?.includes("${")) {
                  const converter = this.convertToMirror[this.fieldDefinition.relationType] || this.convertToMirror[this.fieldDefinition.fieldType];
                  let modifiedValue = converter ? converter(value) : value;

                  if (!fastDeepEqual(this.mirrorObservable(), modifiedValue)) {
                    this.mirrorObservable(modifiedValue);
                  }
                }
              }
            }
          });

          // Capture changes to the mirror and write to the field. Format depends on field type.
          if (this.mirrorPath) {
            this.mirrorObservable = this.currentForm.data[this.mirrorPath].value;
            this.mirrorKnockoutSubscription = this.mirrorObservable.subscribe((value) => {
              if (this.fieldObservable) {
                if (this.fieldDefinition) {

                  // Don't set the field value if it contains a variable reference (these are used in scheduled templates)
                  if (!value?.includes("${")) {
                    const converter = this.convertToField[this.fieldDefinition.relationType] || this.convertToField[this.fieldDefinition.fieldType];
                    let modifiedValue = converter ? converter(value) : value;

                    if (!fastDeepEqual(this.fieldObservable(), modifiedValue)) {
                      this.fieldObservable(modifiedValue);

                      const fieldSettingCallback = this.fieldSettingCallback[this.fieldDefinition.relationType] || this.fieldSettingCallback[this.fieldDefinition.fieldType];
                      if (fieldSettingCallback) {
                        fieldSettingCallback();
                      }
                    }
                  }
                }
              }
            });
          }

          // Define the record for the knockout template
          const record = (window as any).KO_MAPPING.fromJS(this.record);

          // The record doesn't define a needed method
          if (!record.isNewRecord) {
            record.isNewRecord = () => {
              return record._isNewRecord();
            }
          }

          // Define the field definition for the knockout template
          const fieldDefinition = this.fieldDefinition || koFieldDefinition;

          // Define the data for the knockout template
          const data: any = {
            getAspect() {
              return "SelfService";
            },
            "$root": {
              formDisplayType: 'form',
            },
            fieldDefinition: fieldDefinition,
            field: koFieldViewModel,
            record: record,
            data: {
              isNewRecord: () => {
                return true;
              }
            },
          };

          // Add the form display type, as Knockout uses this info for styles
          data.formDisplayType = "form";

          // Add a callback to the binding context that nested templates can use to pass info back up the tree,
          // which can be passed on to containing components.
          data.fieldRenderInfoCallback = (data: any) => {
            this.fieldRenderInfo.emit(data);
          };

          // Render the field renderer.
          (window as any).KO.cleanNode(element);
          (window as any).KO.renderTemplate("nuclear-field-template", data, {}, element, "replaceChildren");

          // We need to do this on the first setting of the related list renderer, at least
          const fieldSettingCallback = this.fieldSettingCallback[this.fieldDefinition.relationType] || this.fieldSettingCallback[this.fieldDefinition.fieldType];
          if (fieldSettingCallback) {
            fieldSettingCallback();
          }

          this.knockoutTemplateRendered = true;

          // Set the mirror to empty - do we want to do this?
          // if (this.mirrorObservable) {
          //   this.mirrorObservable(null);
          // }

        } else {
          // No field specified so remove the rendered field
          element.replaceChildren();
        }
      }
    }
  }

  private buildFieldDefinition(): any {

    if (!this.field) {
      return null;
    }

    // Look in the 'get' view definition for the table, for the field
    let def = this.viewDefinitionFields[this.normaliseFieldName(this.field)];

    // If not there we have to construct one from the table definition
    if (!def) {

      const systemField = {
        id: 'core.id.name',
        Id: 'core.id.name',
        SID: 'core.sid.name',
        CreatedOn: 'core.createdon.name',
        CreatedBy: 'core.createdby.name',
        UpdatedOn: 'core.updatedon.name',
        UpdatedBy: 'core.updatedby.name',
        Version: 'core.version.name',
        TableClass: 'core.tableclass.name',
        Application: 'core.application.name',
      };

      const managedField = {
        WorkflowStatus: 'core.workflowstatus.name',
        PreviousTransition: 'core.previoustransition.name',
        PreviousWorkflowStatus: 'core.previousworkflowstatus.name',
        QuestionsSource: 'core.questionssource.name',
      };

      let fieldDef = this.tableDefinition._fields.find(f => f._fieldName == this.field);
      let relationshipDef = this.tableDefinition._relationships.find(f => f._relationshipName == this.field);

      let i18nKey = systemField[this.field] || `${this.table}.${this.field}.name`.toLowerCase();
      let readOnly = !!systemField[this.field] || false;

      let isSystemField = !!systemField[this.field] || false;
      let isManagedField = !!managedField[this.field] || false;

      if (fieldDef && fieldDef._fieldType !== "REFERENCE") {
        let fieldType = fieldDef._fieldType.toLowerCase();
        let renderer = this.tableMetadataService.allFieldTypes()[fieldType].defaultRenderer;

        def = {
          "properties": {},
          "i18nKey": i18nKey,
          "type": "TABLE_FIELD",
          "placeholderI18nKey": null,
          "tooltipI18nKey": null,
          "customStyles": null,
          "prefixIcon": null,
          "columnClass": null,
          "renderer": renderer,
          "rendererProperties": {},
          "fieldName": this.field,
          "fieldType": fieldType,
          "table": this.table,
          "viewMode": "READ_ONLY",
          "fieldInstructionsI18nKey": null,
          "isSystemField": isSystemField,
          "isManagedField": isManagedField,
          "readOnly": readOnly,
          "required": false,
          "value": {
            "type": "STANDARD_FIELD"
          }
        };
      } else if (relationshipDef) {
        let fieldType = relationshipDef._relationshipType.toLowerCase();
        let renderer = this.tableMetadataService.allFieldTypes()[fieldType].defaultRenderer;

        def = {
          "properties": {},
          "i18nKey": i18nKey,
          "type": "REFERENCE_FIELD",
          "placeholderI18nKey": null,
          "tooltipI18nKey": null,
          "customStyles": null,
          "prefixIcon": null,
          "columnClass": null,
          "renderer": renderer,
          "rendererProperties": {},
          "fieldName": this.field,
          "fieldType": fieldType,
          "table": this.table,
          "viewMode": "READ_ONLY",
          "fieldInstructionsI18nKey": null,
          "isSystemField": isSystemField,
          "isManagedField": isManagedField,
          "readOnly": readOnly,
          "required": false,
          "value": {
            "type": "STANDARD_FIELD"
          },
          "relationType": fieldType,
          "loadType": "SYNC",
          "displayRelations": {},
          "queryFilter": null,
          "displayFields": []
        };
      }
    }

    // Special case for condition.
    // Needs these to dynamically update the model so we don't have to render the Apply button,
    // and to dynamically update the query builder if the model changes.
    if (def.fieldType == "condition") {
      def.rendererProperties['update.model.on.change'] = true;
      def.rendererProperties['update.query.builder.on.change'] = true;
    }

    // Overwrite the renderer if another supplied.
    if (this.renderer) {
      def.renderer = this.renderer;
    }

    // Set the readOnly flags
    def.readOnly = !this.editable;
    def.viewMode = this.editable ? 'EDIT' : 'READ_ONLY';

    this.debugFieldRenderer = def.renderer;
    this.debugFieldType = def.fieldType;

    return def;
  }

  private buildFieldMap(container: any[]): any {
    const result = {};

    function processArray(container: any[]) {
      container.forEach((fieldDefinition) => {
        extractField(fieldDefinition);
      });
    }

    function extractField(element: any) {
      if (UtilService.hasProperty(element, "fieldName")) {
        result["" + element.fieldName] = element;
      }

      if (UtilService.hasProperty(element, "elements")) {
        processArray(element.elements);
      }
    }

    processArray(container);

    return result;
  }

  private tableHasChanged(name: string): boolean {
    return (this.tablePath || this.path || "").split('.')[0] === name;
  }

  private fieldHasChanged(name: string): boolean {
    return (this.fieldPath || this.path || "").split('.').slice(-1)[0] === name;
  }

  private considerFieldValue() {
    return this.tablePath && this.fieldPath;
  }

  private normaliseFieldName(fieldName: string) {
    return fieldName.toLowerCase() == 'id' ? 'Id' : fieldName;
  }

  private changedFieldsIncludesTable(current: any): any[] {
    const changedFields = Object
      .getOwnPropertyNames(current)
      .filter(e => /[A-Z]/.test(e[0]))
      .filter(e => current[e].hasChanged())
      .reduce((acc, e) => {
        acc[e] = current[e]();
        return acc;
      }, {});

    return [changedFields, Object.keys(changedFields).filter(f => this.tableHasChanged(f)).length > 0];
  }

  private async configureContext(current: any, eventName?: string, eventContext?: any, force: boolean = false) {
    this.current = current;
    this.currentForm = eventContext.formViewModel || eventContext.viewModel;

    const [changedFields, relevantFieldHasChanged] = this.changedFieldsIncludesTable(current);

    // We cannot configure unless the record has been saved first as the dot walking happens on the server side.
    if ((current != null && !this.table && !this.field) || force || relevantFieldHasChanged) {

      const data = {
        id: current.getID(),
        table: current.getTableName(),
        changed: changedFields,
        "custom.field.table.path": this.tablePath,
        "custom.field.field.path": this.fieldPath,
        "custom.field.path": this.path,
        "custom.field.table": this.table,
        "custom.field.field": this.field,
        "custom.field.id": this.id,
      };

      const targetResult: any = await this.requestService.createRequest()
        .controller('servicely.core.CustomField')
        .method('getCustomFieldContext')
        .data(data)
        .execute();

      this.table = targetResult.data.targetTable;
      this.field = targetResult.data.targetField;
      this.id = targetResult.data.targetId;
    }
  }
}
